import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Zap, Plus, Trash2, Edit, Loader2, Package } from 'lucide-react';
import { toast } from 'sonner';

export default function OrderBumps() {
  const [bumps, setBumps] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '', product_id: '', trigger_product_ids: [],
    bump_price: '', headline: 'Tambahkan ini ke pesananmu!',
    description: '', image_url: '', is_active: true, display_order: 0
  });

  const fetchData = async () => {
    try {
      const [bumpRes, prodRes] = await Promise.all([
        api.get('/tenant/order-bumps'),
        api.get('/tenant/products?limit=200')
      ]);
      setBumps(bumpRes.data.order_bumps || []);
      setProducts(prodRes.data.products || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const resetForm = () => {
    setForm({ name: '', product_id: '', trigger_product_ids: [], bump_price: '', headline: 'Tambahkan ini ke pesananmu!', description: '', image_url: '', is_active: true, display_order: 0 });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.product_id) { toast.error('Nama dan produk wajib diisi'); return; }
    setSaving(true);
    try {
      const payload = { ...form, bump_price: form.bump_price ? parseInt(form.bump_price) : null };
      if (editingId) {
        await api.put(`/tenant/order-bumps/${editingId}`, payload);
        toast.success('Order bump berhasil diupdate');
      } else {
        await api.post('/tenant/order-bumps', payload);
        toast.success('Order bump berhasil dibuat');
      }
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleEdit = (b) => {
    setForm({
      name: b.name, product_id: b.product_id, trigger_product_ids: b.trigger_product_ids || [],
      bump_price: b.bump_price || '', headline: b.headline || '', description: b.description || '',
      image_url: b.image_url || '', is_active: b.is_active, display_order: b.display_order || 0
    });
    setEditingId(b.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus order bump ini?')) return;
    try {
      await api.delete(`/tenant/order-bumps/${id}`);
      toast.success('Order bump dihapus');
      fetchData();
    } catch (e) { toast.error('Gagal menghapus'); }
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin h-8 w-8 text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="order-bumps-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Order Bumps</h1>
          <p className="text-sm text-slate-500">Tambahkan penawaran spesial di halaman checkout</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="create-bump-btn">
          <Plus size={16} className="mr-2" /> Buat Order Bump
        </Button>
      </div>

      {showForm && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">{editingId ? 'Edit Order Bump' : 'Buat Order Bump Baru'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nama *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama bump" data-testid="bump-name-input" />
                </div>
                <div>
                  <Label>Produk yang Ditawarkan *</Label>
                  <select className="w-full border border-slate-200 rounded-md p-2 text-sm" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="bump-product-select">
                    <option value="">Pilih produk...</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.name} - Rp {fmt(p.price)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Harga Bump (kosong = harga asli)</Label>
                  <Input type="number" value={form.bump_price} onChange={(e) => setForm({ ...form, bump_price: e.target.value })} placeholder="Harga spesial" />
                </div>
                <div>
                  <Label>Urutan</Label>
                  <Input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })} />
                </div>
              </div>
              <div>
                <Label>Headline</Label>
                <Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="Tambahkan ini ke pesananmu!" data-testid="bump-headline-input" />
              </div>
              <div>
                <Label>Deskripsi</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Deskripsi penawaran..." rows={2} />
              </div>
              <div>
                <Label>URL Gambar</Label>
                <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label>Aktif</Label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={saving} data-testid="save-bump-btn">
                  {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                  {editingId ? 'Update' : 'Simpan'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>Batal</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {bumps.length === 0 ? (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="py-12 text-center">
              <Zap size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Belum ada order bump</p>
            </CardContent>
          </Card>
        ) : (
          bumps.map(b => (
            <Card key={b.id} className="border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                      {b.image_url ? (
                        <img src={b.image_url} alt={b.name} className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <Zap size={20} className="text-amber-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{b.name}</span>
                        <Badge variant={b.is_active ? 'default' : 'secondary'}>
                          {b.is_active ? 'Aktif' : 'Nonaktif'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500">{b.headline}</p>
                      <p className="text-xs text-slate-400">
                        Produk: {b.product?.name || b.product_id}
                        {b.bump_price && ` · Harga bump: Rp ${fmt(b.bump_price)}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(b)}>
                      <Edit size={14} className="mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDelete(b.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
